/**
 * Sistema de Permisos RBAC de 3 Niveles
 * 
 * Nivel 1: Roles (AGENCY_ADMIN, AGENCY_AGENT_USER, BUYER_CLIENT, etc.)
 * Nivel 2: Recursos (quotes, properties, clients, etc.)
 * Nivel 3: Acciones (create, read, update, delete, accept, etc.)
 */

// ===== ROLES =====
export enum UserRole {
  // Roles Globales (CORE)
  SUPER_ADMIN = 'SUPER_ADMIN',

  // Roles de Negocio (BUSINESS)
  AGENCY_ADMIN = 'AGENCY_ADMIN',
  BROKER_MANAGER_ADMIN = 'BROKER_MANAGER_ADMIN',
  BROKER_MANAGER_USER = 'BROKER_MANAGER_USER',
  PROJECT_DEVELOPER_ADMIN = 'PROJECT_DEVELOPER_ADMIN',
  AGENCY_AGENT_USER = 'AGENCY_AGENT_USER',

  // Roles de Soporte (SUPPORT)
  ACCOUNTING_TEAM = 'ACCOUNTING_TEAM',
  OPERATIONAL_TEAM = 'OPERATIONAL_TEAM',
  CONDOMINIUM_ADMIN = 'CONDOMINIUM_ADMIN',

  // Roles Especializados (SPECIALIZED)
  LEGAL_MODULE = 'LEGAL_MODULE',

  // Roles de Cliente (CLIENT)
  BUYER_CLIENT = 'BUYER_CLIENT',

  // Rol de Usuario en Onboarding
  PENDING_ONBOARDING = 'PENDING_ONBOARDING',

  // @deprecated - Mantenido solo para compatibilidad con BD (migrar a AGENCY_AGENT_USER).
  // Coherencia con el enum backend, que lo sigue honrando en decoradores @Roles.
  REAL_ESTATE_AGENT_ADMIN = 'REAL_ESTATE_AGENT_ADMIN',
}

// ===== RECURSOS =====
export enum Resource {
  // Recursos de Agencia
  QUOTES = 'quotes',
  PROPERTIES = 'properties',
  CLIENTS = 'clients',
  APPOINTMENTS = 'appointments',
  SALES = 'sales',
  REPORTS = 'reports',
  COMMISSIONS = 'commissions',
  AGENTS = 'agents',
  
  // Recursos de Cliente
  FAVORITES = 'favorites',
  SEARCHES = 'searches',
  REQUESTS = 'requests',
  
  // Recursos de Condominio
  RESIDENTS = 'residents',
  PAYMENTS = 'payments',
  MAINTENANCE = 'maintenance',
  MEETINGS = 'meetings',
  
  // Recursos de Promotora
  PROJECTS = 'projects',
  UNITS = 'units',
  LEADS = 'leads',
  MARKETING = 'marketing',
  
  // Recursos de Operaciones
  INCIDENTS = 'incidents',
  CLEANING = 'cleaning',
  STAFF = 'staff',
  SCHEDULES = 'schedules',
  
  // Recursos del Sistema
  USERS = 'users',
  TENANTS = 'tenants',
  NOTIFICATIONS = 'notifications',
  SETTINGS = 'settings',
}

// ===== ACCIONES =====
export enum Action {
  CREATE = 'create',
  READ = 'read',
  UPDATE = 'update',
  DELETE = 'delete',
  
  // Acciones específicas de cotizaciones
  ACCEPT_QUOTE = 'accept_quote',
  REJECT_QUOTE = 'reject_quote',
  SEND_QUOTE = 'send_quote',
  DUPLICATE_QUOTE = 'duplicate_quote',
  
  // Acciones específicas de propiedades
  PUBLISH_PROPERTY = 'publish_property',
  UNPUBLISH_PROPERTY = 'unpublish_property',
  ASSIGN_AGENT = 'assign_agent',
  
  // Acciones específicas de clientes
  ASSIGN_CLIENT = 'assign_client',
  TRANSFER_CLIENT = 'transfer_client',
  
  // Acciones específicas de citas
  SCHEDULE_APPOINTMENT = 'schedule_appointment',
  CANCEL_APPOINTMENT = 'cancel_appointment',
  RESCHEDULE_APPOINTMENT = 'reschedule_appointment',
  
  // Acciones específicas de ventas
  REGISTER_SALE = 'register_sale',
  APPROVE_SALE = 'approve_sale',
  REJECT_SALE = 'reject_sale',
  
  // Acciones administrativas
  MANAGE_USERS = 'manage_users',
  MANAGE_PERMISSIONS = 'manage_permissions',
  VIEW_ANALYTICS = 'view_analytics',
  EXPORT_DATA = 'export_data',
  
  // Acciones de sistema
  SYSTEM_CONFIG = 'system_config',
  BACKUP_DATA = 'backup_data',
  RESTORE_DATA = 'restore_data',
}

// ===== PERMISOS =====
export type Permission = `${Resource}:${Action}`;

// ===== INTERFACES =====
export interface UserPermissions {
  role: UserRole;
  permissions: Permission[];
  tenantId?: string;
  userId: string;
}

export interface PermissionCheck {
  resource: Resource;
  action: Action;
  role: UserRole;
}

export interface RBACConfig {
  roles: Record<UserRole, Permission[]>;
  defaultPermissions: Permission[];
}

// ===== MAPEO DE PERMISOS POR ROL =====
export const RBAC_PERMISSIONS: RBACConfig = {
  roles: {
    [UserRole.AGENCY_ADMIN]: [
      // Permisos completos sobre cotizaciones
      `${Resource.QUOTES}:${Action.CREATE}`,
      `${Resource.QUOTES}:${Action.READ}`,
      `${Resource.QUOTES}:${Action.UPDATE}`,
      `${Resource.QUOTES}:${Action.DELETE}`,
      `${Resource.QUOTES}:${Action.ACCEPT_QUOTE}`,
      `${Resource.QUOTES}:${Action.REJECT_QUOTE}`,
      `${Resource.QUOTES}:${Action.SEND_QUOTE}`,
      `${Resource.QUOTES}:${Action.DUPLICATE_QUOTE}`,
      
      // Permisos sobre propiedades
      `${Resource.PROPERTIES}:${Action.CREATE}`,
      `${Resource.PROPERTIES}:${Action.READ}`,
      `${Resource.PROPERTIES}:${Action.UPDATE}`,
      `${Resource.PROPERTIES}:${Action.DELETE}`,
      `${Resource.PROPERTIES}:${Action.PUBLISH_PROPERTY}`,
      `${Resource.PROPERTIES}:${Action.UNPUBLISH_PROPERTY}`,
      `${Resource.PROPERTIES}:${Action.ASSIGN_AGENT}`,
      
      // Permisos sobre clientes
      `${Resource.CLIENTS}:${Action.CREATE}`,
      `${Resource.CLIENTS}:${Action.READ}`,
      `${Resource.CLIENTS}:${Action.UPDATE}`,
      `${Resource.CLIENTS}:${Action.DELETE}`,
      `${Resource.CLIENTS}:${Action.ASSIGN_CLIENT}`,
      `${Resource.CLIENTS}:${Action.TRANSFER_CLIENT}`,
      
      // Permisos sobre agentes
      `${Resource.AGENTS}:${Action.CREATE}`,
      `${Resource.AGENTS}:${Action.READ}`,
      `${Resource.AGENTS}:${Action.UPDATE}`,
      `${Resource.AGENTS}:${Action.DELETE}`,
      `${Resource.AGENTS}:${Action.MANAGE_USERS}`,
      
      // Permisos sobre citas
      `${Resource.APPOINTMENTS}:${Action.CREATE}`,
      `${Resource.APPOINTMENTS}:${Action.READ}`,
      `${Resource.APPOINTMENTS}:${Action.UPDATE}`,
      `${Resource.APPOINTMENTS}:${Action.DELETE}`,
      `${Resource.APPOINTMENTS}:${Action.SCHEDULE_APPOINTMENT}`,
      `${Resource.APPOINTMENTS}:${Action.CANCEL_APPOINTMENT}`,
      `${Resource.APPOINTMENTS}:${Action.RESCHEDULE_APPOINTMENT}`,
      
      // Permisos sobre ventas
      `${Resource.SALES}:${Action.CREATE}`,
      `${Resource.SALES}:${Action.READ}`,
      `${Resource.SALES}:${Action.UPDATE}`,
      `${Resource.SALES}:${Action.DELETE}`,
      `${Resource.SALES}:${Action.REGISTER_SALE}`,
      `${Resource.SALES}:${Action.APPROVE_SALE}`,
      `${Resource.SALES}:${Action.REJECT_SALE}`,
      
      // Permisos sobre reportes
      `${Resource.REPORTS}:${Action.READ}`,
      `${Resource.REPORTS}:${Action.EXPORT_DATA}`,
      `${Resource.REPORTS}:${Action.VIEW_ANALYTICS}`,
      
      // Permisos sobre comisiones
      `${Resource.COMMISSIONS}:${Action.READ}`,
      `${Resource.COMMISSIONS}:${Action.UPDATE}`,
      `${Resource.COMMISSIONS}:${Action.EXPORT_DATA}`,
    ],
    
    [UserRole.AGENCY_AGENT_USER]: [
      // Permisos limitados sobre cotizaciones (SIN aceptar)
      `${Resource.QUOTES}:${Action.CREATE}`,
      `${Resource.QUOTES}:${Action.READ}`,
      `${Resource.QUOTES}:${Action.UPDATE}`,
      `${Resource.QUOTES}:${Action.SEND_QUOTE}`,
      `${Resource.QUOTES}:${Action.DUPLICATE_QUOTE}`,
      // NO tiene: ACCEPT_QUOTE, REJECT_QUOTE, DELETE
      
      // Permisos sobre propiedades
      `${Resource.PROPERTIES}:${Action.CREATE}`,
      `${Resource.PROPERTIES}:${Action.READ}`,
      `${Resource.PROPERTIES}:${Action.UPDATE}`,
      // NO tiene: DELETE, PUBLISH_PROPERTY, UNPUBLISH_PROPERTY, ASSIGN_AGENT
      
      // Permisos sobre clientes
      `${Resource.CLIENTS}:${Action.CREATE}`,
      `${Resource.CLIENTS}:${Action.READ}`,
      `${Resource.CLIENTS}:${Action.UPDATE}`,
      // NO tiene: DELETE, ASSIGN_CLIENT, TRANSFER_CLIENT
      
      // Permisos sobre citas
      `${Resource.APPOINTMENTS}:${Action.CREATE}`,
      `${Resource.APPOINTMENTS}:${Action.READ}`,
      `${Resource.APPOINTMENTS}:${Action.UPDATE}`,
      `${Resource.APPOINTMENTS}:${Action.SCHEDULE_APPOINTMENT}`,
      `${Resource.APPOINTMENTS}:${Action.CANCEL_APPOINTMENT}`,
      `${Resource.APPOINTMENTS}:${Action.RESCHEDULE_APPOINTMENT}`,
      
      // Permisos sobre ventas
      `${Resource.SALES}:${Action.CREATE}`,
      `${Resource.SALES}:${Action.READ}`,
      `${Resource.SALES}:${Action.UPDATE}`,
      `${Resource.SALES}:${Action.REGISTER_SALE}`,
      // NO tiene: DELETE, APPROVE_SALE, REJECT_SALE
      
      // Permisos sobre reportes (solo lectura)
      `${Resource.REPORTS}:${Action.READ}`,
      `${Resource.REPORTS}:${Action.EXPORT_DATA}`,
      
      // Permisos sobre comisiones (solo lectura)
      `${Resource.COMMISSIONS}:${Action.READ}`,
    ],
    
    [UserRole.BUYER_CLIENT]: [
      // Permisos limitados del cliente
      `${Resource.PROPERTIES}:${Action.READ}`,
      `${Resource.QUOTES}:${Action.READ}`,
      `${Resource.APPOINTMENTS}:${Action.CREATE}`,
      `${Resource.APPOINTMENTS}:${Action.READ}`,
      `${Resource.APPOINTMENTS}:${Action.UPDATE}`,
      `${Resource.FAVORITES}:${Action.CREATE}`,
      `${Resource.FAVORITES}:${Action.READ}`,
      `${Resource.FAVORITES}:${Action.DELETE}`,
      `${Resource.SEARCHES}:${Action.CREATE}`,
      `${Resource.SEARCHES}:${Action.READ}`,
    ],
    
    // Otros roles sin permisos específicos definidos
    [UserRole.SUPER_ADMIN]: [], // Super Admin tiene acceso a todo sin necesidad de permisos específicos
    [UserRole.BROKER_MANAGER_ADMIN]: [],
    [UserRole.BROKER_MANAGER_USER]: [],
    [UserRole.PROJECT_DEVELOPER_ADMIN]: [],
    [UserRole.CONDOMINIUM_ADMIN]: [],
    [UserRole.ACCOUNTING_TEAM]: [],
    [UserRole.OPERATIONAL_TEAM]: [],
    [UserRole.LEGAL_MODULE]: [],
    [UserRole.PENDING_ONBOARDING]: [], // No tiene permisos, debe completar onboarding
    [UserRole.REAL_ESTATE_AGENT_ADMIN]: [], // @deprecated - gating por rol como AGENCY_AGENT_USER
  },
  
  defaultPermissions: [
    `${Resource.NOTIFICATIONS}:${Action.READ}`,
    `${Resource.SETTINGS}:${Action.READ}`,
  ],
};

// ===== UTILIDADES =====
export const createPermission = (resource: Resource, action: Action): Permission => {
  return `${resource}:${action}`;
};

export const hasPermission = (userPermissions: UserPermissions, resource: Resource, action: Action): boolean => {
  const permission = createPermission(resource, action);
  return userPermissions.permissions.includes(permission);
};

export const hasRole = (userPermissions: UserPermissions, role: UserRole): boolean => {
  return userPermissions.role === role;
};

export const getPermissionsForRole = (role: UserRole): Permission[] => {
  return RBAC_PERMISSIONS.roles[role] || [];
};

export const isAgencyAdmin = (userPermissions: UserPermissions): boolean => {
  return hasRole(userPermissions, UserRole.AGENCY_ADMIN);
};

export const isAgencyAgent = (userPermissions: UserPermissions): boolean => {
  return hasRole(userPermissions, UserRole.AGENCY_AGENT_USER);
};

export const canAcceptQuotes = (userPermissions: UserPermissions): boolean => {
  return hasPermission(userPermissions, Resource.QUOTES, Action.ACCEPT_QUOTE);
};

export const canRejectQuotes = (userPermissions: UserPermissions): boolean => {
  return hasPermission(userPermissions, Resource.QUOTES, Action.REJECT_QUOTE);
};
